/**
 * The typed fetch wrapper for the operator API (design 12.5).
 *
 * The SPA calls the BFF at the SAME ORIGIN under `/api/v1/*`. The BFF (src/worker/index.ts) owns
 * auth, the identity envelope and the trusted request id; the SPA never sees the envelope, mints
 * nothing, and sends no tenant/role (those come from the verified Access identity on the server).
 * So this wrapper is deliberately thin: it builds the `/api/v1` URL, sends cookies (Cloudflare
 * Access is a cookie/redirect flow at this origin, so credentials must be included), sets a
 * JSON `Accept`, and turns the BFF's response into a typed result — in particular it recognises the
 * BFF's 503 `unavailable` "waking" answer so the shell can show "Waking the control plane" with retry
 * (design 12.3, NFR-7 AC1), and tells it apart from a hosted service starting up
 * (`service_unavailable`), which must not raise that core banner.
 *
 * No real screen queries live here; those arrive with the screens (tasks 11.4+). This is the one
 * place that knows the base path and the error envelope, so every later query shares them.
 */

/** The base every operator-API call is built on. Same origin; the BFF proxies to `core`. */
export const API_BASE = "/api/v1";

/** The BFF's error body (src/worker/security.ts `apiError` / `coreUnavailable`). */
export interface ApiErrorBody {
  readonly error: {
    readonly code: string;
    readonly message: string;
    readonly request_id: string;
  };
}

/**
 * A failed API call. `kind` lets the UI branch without parsing status codes everywhere:
 * - `waking`      — 503 with the `unavailable` (or core `waking`) code: core is cold/unreachable;
 *                   retry and raise the shell banner (NFR-7 AC1).
 * - `service_unavailable` — 503 with the `service_unavailable` code: a hosted service behind the
 *                   service-admin proxy (IDP/AIS/VCI) is starting; retry, scoped to that view only.
 * - `unauthorized`— 401: the operator session is missing or expired; the app should prompt re-auth.
 * - `forbidden`   — 403: authenticated but not allowed (unprovisioned, or role too low).
 * - `not_found`   — 404.
 * - `request`     — any other 4xx the server rejected.
 * - `server`      — any other 5xx, including a 503 that carries neither code above.
 * - `network`     — the request never got a response (offline, DNS, abort).
 * - `malformed`   — a response that was not the expected JSON shape.
 */
export type ApiErrorKind =
  | "waking"
  | "service_unavailable"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "request"
  | "server"
  | "network"
  | "malformed";

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  /** The HTTP status, or null when there was no response (network/abort). */
  readonly status: number | null;
  /** The server error code (`unavailable`, `forbidden`, …), when the body carried one. */
  readonly code: string | null;
  /** The trusted request id echoed by the BFF (NFR-9.2), for support/correlation, when present. */
  readonly requestId: string | null;
  /** Seconds the server asked us to wait before retrying (from `Retry-After`), when present. */
  readonly retryAfterSeconds: number | null;

  constructor(
    kind: ApiErrorKind,
    message: string,
    options: {
      status?: number | null;
      code?: string | null;
      requestId?: string | null;
      retryAfterSeconds?: number | null;
      cause?: unknown;
    } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = "ApiError";
    this.kind = kind;
    this.status = options.status ?? null;
    this.code = options.code ?? null;
    this.requestId = options.requestId ?? null;
    this.retryAfterSeconds = options.retryAfterSeconds ?? null;
  }

  /** True when the error is the cold-core "waking" condition the shell shows with a retry. */
  get isWaking(): boolean {
    return this.kind === "waking";
  }
}

/** Options for a single API request. */
export interface ApiRequestInit {
  /** HTTP method; defaults to GET. */
  readonly method?: string;
  /** Query parameters appended to the path (undefined values are omitted). */
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  /** A JSON body; serialised and sent with `content-type: application/json`. */
  readonly body?: unknown;
  /** An abort signal so callers (and TanStack Query) can cancel. */
  readonly signal?: AbsoluteSignal;
}

/** Narrower alias so the public surface does not depend on the DOM `AbortSignal` name directly. */
type AbsoluteSignal = AbortSignal;

function buildUrl(path: string, query: ApiRequestInit["query"]): string {
  const normalised = path.startsWith("/") ? path : `/${path}`;
  const url = `${API_BASE}${normalised}`;
  if (query === undefined) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs.length > 0 ? `${url}?${qs}` : url;
}

function parseRetryAfter(headerValue: string | null): number | null {
  if (headerValue === null) return null;
  const seconds = Number(headerValue);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== "object" || value === null) return false;
  const error = (value as { error?: unknown }).error;
  if (typeof error !== "object" || error === null) return false;
  const e = error as Record<string, unknown>;
  return typeof e["code"] === "string" && typeof e["message"] === "string";
}

const REQUEST_ID_HEADER = "x-request-id";

/**
 * Perform an API call and return the parsed JSON body typed as `T`, or throw an {@link ApiError}.
 * `T` is the caller's responsibility to match the contract response; this wrapper does not validate
 * the success body (a screen that needs validation can run the `@oathera/contract` validator).
 */
export async function apiFetch<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const url = buildUrl(path, init.query);
  const headers = new Headers({ accept: "application/json" });
  let requestBody: string | undefined;
  if (init.body !== undefined) {
    headers.set("content-type", "application/json");
    requestBody = JSON.stringify(init.body);
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: init.method ?? "GET",
      headers,
      // Cloudflare Access authenticates at this origin with a cookie; it must be sent.
      credentials: "same-origin",
      ...(requestBody === undefined ? {} : { body: requestBody }),
      ...(init.signal === undefined ? {} : { signal: init.signal }),
    });
  } catch (cause) {
    // No response at all: offline, aborted, DNS. The shell still runs; a live view would degrade.
    throw new ApiError("network", "The request could not be sent.", { cause });
  }

  const requestId = response.headers.get(REQUEST_ID_HEADER);

  if (response.ok) {
    // 204 and other empty bodies resolve to undefined; callers that expect a body type a non-void T.
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    if (text.length === 0) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch (cause) {
      throw new ApiError("malformed", "The server returned a response that was not valid JSON.", {
        status: response.status,
        requestId,
        cause,
      });
    }
  }

  // Error path: read the envelope if present so we can carry the code and request id.
  let code: string | null = null;
  let message = `Request failed with status ${response.status}.`;
  try {
    const parsed: unknown = await response.json();
    if (isApiErrorBody(parsed)) {
      code = parsed.error.code;
      message = parsed.error.message;
    } else if (
      // The team-management routes answer with the AgentDNA envelope `{ status, message, data }`
      // (not `{ error: { code, message } }`), so surface its `message` on an error rather than the
      // generic "Request failed" fallback.
      typeof parsed === "object" &&
      parsed !== null &&
      (parsed as { status?: unknown }).status === false &&
      typeof (parsed as { message?: unknown }).message === "string" &&
      ((parsed as { message: string }).message).length > 0
    ) {
      message = (parsed as { message: string }).message;
    }
  } catch {
    // A non-JSON error body is fine; the status still classifies the failure below.
  }

  const retryAfterSeconds = parseRetryAfter(response.headers.get("retry-after"));
  const base = { status: response.status, code, requestId, retryAfterSeconds } as const;

  // 503 classification. Only the BFF's `unavailable` code (coreUnavailable, also core's own 503 code)
  // or core's contract `waking` code is the cold-core "waking" state that raises the shell banner
  // (NFR-7 AC1). The service-admin proxy's `service_unavailable` is a hosted service starting up: its
  // own kind, retried, never the core banner. Any other 503 is a plain server error.
  if (response.status === 503) {
    if (code === "unavailable" || code === "waking") throw new ApiError("waking", message, base);
    if (code === "service_unavailable") throw new ApiError("service_unavailable", message, base);
    throw new ApiError("server", message, base);
  }
  if (response.status === 401) throw new ApiError("unauthorized", message, base);
  if (response.status === 403) throw new ApiError("forbidden", message, base);
  if (response.status === 404) throw new ApiError("not_found", message, base);
  if (response.status >= 500) throw new ApiError("server", message, base);
  throw new ApiError("request", message, base);
}
